"use client";

import { Badge } from "@lattice/frontend/components/ui/badge";
import { Button } from "@lattice/frontend/components/ui/button";
import {
  BILLING_PURCHASE_INTENTS_BY_PLAN_PERIOD,
  E_BillingCatalogSurface,
  E_BillingClientPlatform,
  E_BillingPeriod,
  E_BillingPlanFeatureState,
  E_BillingPlanKey,
  type T_BillingCatalog,
  type T_BillingPaidPlanKey,
  type T_BillingPlan,
  type T_BillingPurchaseOption,
} from "@lattice/core-lib/types/billing/billing-catalog.types";
import {
  configureRevenueCat,
  ErrorCode,
  PurchasesError,
} from "@lattice/frontend/lib/revenuecat";
import type { Package as T_RevenueCatPackage } from "@revenuecat/purchases-js";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import { motion } from "framer-motion";
import { Check, ChevronRight, Crown, Lock, Sparkles } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React from "react";
import { toast } from "sonner";
import { cn } from "@lattice/frontend/lib/utils/ui";
import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";
import { createWebSettingsHref } from "@lattice/frontend/features/shell/web-settings-intent";

// ============================================================================
// Types & Constants
// ============================================================================

type T_PricingHeroProps = {
  revenuecat_api_key: string;
  is_authed: boolean;
  app_environment: string;
};

type T_Tier = "FREE" | "PRO" | "MAX";
type T_PaidTier = T_BillingPaidPlanKey;

const TIER_RANK = { FREE: 0, PRO: 1, MAX: 2 } as const;
const PAID_TIERS: readonly T_PaidTier[] = [
  E_BillingPlanKey.PRO,
  E_BillingPlanKey.MAX,
];

const billing_periods = ["Monthly", "Annual"] as const;
type T_BillingPeriod = (typeof billing_periods)[number];

type T_PriceDisplay = {
  price: string;
  priceSuffix: string;
  subprice: string | null;
};

type T_WebPackageMap = Partial<Record<T_PaidTier, T_RevenueCatPackage>>;

const PUBLIC_FALLBACK_PRICING = {
  PRO: {
    monthly: "$14.99",
    yearly: "$149.99",
    annualMonthlyEquivalent: "$12.50",
  },
  MAX: {
    monthly: "$29.99",
    yearly: "$299.99",
    annualMonthlyEquivalent: "$25.00",
  },
} as const satisfies Record<
  T_PaidTier,
  {
    monthly: string;
    yearly: string;
    annualMonthlyEquivalent: string;
  }
>;

function getCatalogPlan(
  catalog: T_BillingCatalog,
  planKey: T_Tier,
): T_BillingPlan | null {
  return (
    catalog.plans.find((candidate) => candidate.plan_key === planKey) ?? null
  );
}

function getPurchaseOption(
  catalog: T_BillingCatalog,
  tier: T_PaidTier,
  isMonthly: boolean,
): T_BillingPurchaseOption | null {
  const intent =
    BILLING_PURCHASE_INTENTS_BY_PLAN_PERIOD[tier][
      isMonthly ? E_BillingPeriod.MONTHLY : E_BillingPeriod.ANNUAL
    ];
  return (
    catalog.purchase_options.find(
      (option) => option.purchase_intent === intent,
    ) ?? null
  );
}

function formatMicrosPerMonth(price: {
  amountMicros: number;
  currency: string;
  formattedPrice: string;
}): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: price.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price.amountMicros / 1_000_000 / 12);
  } catch {
    return price.formattedPrice;
  }
}

function getPlanPriceDisplay(
  pkg: T_RevenueCatPackage | null,
  tier: T_PaidTier,
  isMonthly: boolean,
  usePublicFallback: boolean,
): T_PriceDisplay {
  if (!pkg) {
    if (usePublicFallback) {
      const fallback = PUBLIC_FALLBACK_PRICING[tier];
      return isMonthly
        ? {
            price: fallback.monthly,
            priceSuffix: "/mo",
            subprice: null,
          }
        : {
            price: fallback.annualMonthlyEquivalent,
            priceSuffix: "/mo",
            subprice: `${fallback.yearly}/year`,
          };
    }

    return {
      price: "Unavailable",
      priceSuffix: "",
      subprice: "Plan unavailable",
    };
  }

  const product = pkg.webBillingProduct;
  const price = product.price;
  if (isMonthly) {
    return {
      price: price.formattedPrice,
      priceSuffix: "/mo",
      subprice: null,
    };
  }

  return {
    price: formatMicrosPerMonth(price),
    priceSuffix: "/mo",
    subprice: `${price.formattedPrice}/year`,
  };
}

function getPlanBillingNote(
  pkg: T_RevenueCatPackage | null,
  tier: T_PaidTier,
  purchaseOption: T_BillingPurchaseOption | null,
  isMonthly: boolean,
  hasTrialOffer: boolean,
  usePublicFallback: boolean,
) {
  if (!isMonthly) {
    const promo = purchaseOption?.promo_label
      ? `${purchaseOption.promo_label} with annual billing.`
      : "Annual billing. Cancel anytime.";
    return `${hasTrialOffer ? "7-day free trial included. " : ""}${promo}`;
  }

  if (!pkg) {
    if (usePublicFallback) {
      const fallback = PUBLIC_FALLBACK_PRICING[tier];
      return `${hasTrialOffer ? "7-day free trial, then " : ""}${fallback.monthly}/mo. Cancel anytime.`;
    }

    return "Plan unavailable right now.";
  }

  return `${hasTrialOffer ? "7-day free trial, then " : ""}${pkg.webBillingProduct.price.formattedPrice}/mo. Cancel anytime.`;
}

function PlanPriceDisplay({
  pkg,
  tier,
  isMonthly,
  usePublicFallback,
}: {
  pkg: T_RevenueCatPackage | null;
  tier: T_PaidTier;
  isMonthly: boolean;
  usePublicFallback: boolean;
}) {
  const priceDisplay = getPlanPriceDisplay(
    pkg,
    tier,
    isMonthly,
    usePublicFallback,
  );

  return (
    <div>
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        <span className="text-5xl font-bold leading-none text-white">
          {priceDisplay.price}
        </span>
        <span className="pb-1 text-base font-semibold text-white/75">
          {priceDisplay.priceSuffix}
        </span>
      </div>
      {priceDisplay.subprice ? (
        <p className="mt-2 text-sm leading-5 text-white/35">
          {priceDisplay.subprice}
        </p>
      ) : null}
    </div>
  );
}

function getCurrentPlanCopy(tier: T_Tier, isAuthenticated: boolean) {
  if (!isAuthenticated || tier === "FREE") {
    return {
      label: isAuthenticated ? "Current plan" : "Available today",
      title: "Free",
      description: "Baseline Prism and curated finance news",
      note: "Upgrade when you need more research, event alerts, and portfolio insight.",
    };
  }

  if (tier === "PRO") {
    return {
      label: "Current plan",
      title: "Pro",
      description: "Personalized feed, event alerts, and premium models",
      note: "Max unlocks 5x the Prism usage, best AI models, real-time event alerts, and premium portfolio insights.",
    };
  }

  return {
    label: "Current plan",
    title: "Max",
    description: "Best models, real-time event alerts, and portfolio insights",
    note: "You already have the highest Lattice access.",
  };
}

function CurrentPlanCard({
  tier,
  isAuthenticated,
}: {
  tier: T_Tier;
  isAuthenticated: boolean;
}) {
  const copy = getCurrentPlanCopy(tier, isAuthenticated);
  const isPaidTier = isAuthenticated && tier !== "FREE";

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.25 }}
      className={cn(
        "md:col-span-3 rounded-2xl border p-5",
        isPaidTier
          ? "border-primary/30 bg-primary/10"
          : "border-white/10 bg-white/[0.03]",
      )}
    >
      <p
        className={cn(
          "text-xs font-semibold uppercase tracking-normal",
          isPaidTier ? "text-primary" : "text-white/40",
        )}
      >
        {copy.label}
      </p>
      <div className="mt-2 space-y-1">
        <h2 className="text-2xl font-semibold text-white">{copy.title}</h2>
        <p className="text-base text-white/60">{copy.description}</p>
      </div>
      <p className="mt-3 text-sm leading-5 text-white/40">{copy.note}</p>
    </motion.div>
  );
}

function getPurchaseSuccessMessage(
  tier: T_PaidTier,
  isMonthly: boolean,
  hasTrialOffer: boolean,
  pkg: T_RevenueCatPackage,
) {
  const planName = tier === "PRO" ? "Pro" : "Max";
  const price = pkg.webBillingProduct.price.formattedPrice;

  if (!hasTrialOffer) {
    return `Welcome to ${planName}. Your subscription is active.`;
  }

  if (isMonthly) {
    return `Welcome to ${planName}. Your 7-day free trial has started. Monthly billing starts at ${price}/mo after the trial.`;
  }

  return `Welcome to ${planName}. Your 7-day free trial has started. Annual billing starts at ${price}/year after the trial.`;
}

// ============================================================================
// Pricing Hero
// ============================================================================

export default function PricingHero({
  revenuecat_api_key,
  is_authed,
  app_environment,
}: T_PricingHeroProps) {
  const [selectedPeriod, setSelectedPeriod] =
    React.useState<T_BillingPeriod>("Annual");
  const [purchasingTier, setPurchasingTier] = React.useState<
    "PRO" | "MAX" | null
  >(null);
  const router = useRouter();
  const utils = trpcApi.useUtils();

  const currentUserQuery = trpcApi.users.get_current_user.useQuery(
    {},
    {
      enabled: is_authed,
      retry: false,
    },
  );
  const current_user = currentUserQuery.data;
  const accountPending = is_authed && currentUserQuery.isLoading;
  const hasTrialOffer =
    !is_authed || accountPending || current_user?.Subscription === null;
  const isMonthly = selectedPeriod === "Monthly";

  const billingCatalogQuery = trpcApi.payment.get_billing_catalog.useQuery(
    {
      surface: E_BillingCatalogSurface.WEB_PRICING,
      platform: E_BillingClientPlatform.WEB,
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );
  const billingCatalog = billingCatalogQuery.data?.data ?? null;
  const [packagesByTier, setPackagesByTier] = React.useState<T_WebPackageMap>(
    {},
  );
  const [productsLoading, setProductsLoading] = React.useState(false);

  React.useEffect(() => {
    trackEvent(MixpanelEvents.PAYWALL_VIEWED);
  }, []);

  const user_tier: T_Tier = (() => {
    if (!current_user?.is_premium) return "FREE";
    const sub = current_user.Subscription;
    if (sub?.tier === "MAX") return "MAX";
    if (sub?.tier === "PRO") return "PRO";
    return "FREE";
  })();

  React.useEffect(() => {
    setPackagesByTier({});

    if (!billingCatalog || !current_user) return;

    const catalog = billingCatalog;
    const userCuid = current_user.cuid;
    let cancelled = false;
    setProductsLoading(true);

    async function loadProducts() {
      try {
        const rc_user_id = `${userCuid}_${app_environment}`;
        const purchases = configureRevenueCat(revenuecat_api_key, rc_user_id);
        const offerings = await purchases.getOfferings();
        const selectedOffering =
          offerings.all[catalog.offering.revenuecat_offering_id] ??
          (catalog.offering.key === "standard" ? offerings.current : null);
        const nextPackages: T_WebPackageMap = {};

        for (const tier of PAID_TIERS) {
          const option = getPurchaseOption(catalog, tier, isMonthly);
          const pkg = option
            ? selectedOffering?.packagesById[option.revenuecat_package_id]
            : undefined;
          if (pkg) nextPackages[tier] = pkg;
        }

        if (!cancelled) setPackagesByTier(nextPackages);
      } catch (error) {
        console.warn("[pricing] Failed to load RevenueCat products", error);
        if (!cancelled) setPackagesByTier({});
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    }

    void loadProducts();

    return () => {
      cancelled = true;
    };
  }, [
    app_environment,
    billingCatalog,
    current_user,
    isMonthly,
    revenuecat_api_key,
  ]);

  const handleUpgrade = async (target_tier: "PRO" | "MAX") => {
    if (!is_authed) {
      window.location.href = "/api/auth/sign-in?redirect=pricing";
      return;
    }

    if (!current_user) {
      toast.error("Account details are still loading. Please try again.");
      return;
    }

    if (!billingCatalog) {
      toast.error("Pricing is still loading. Please try again.");
      return;
    }

    const rcPackage = packagesByTier[target_tier] ?? null;
    if (!rcPackage) {
      toast.error("Selected plan is not available. Please try again later.");
      return;
    }

    trackEvent(MixpanelEvents.PURCHASE_INITIATED, {
      plan: target_tier,
      period: selectedPeriod,
    });
    setPurchasingTier(target_tier);
    try {
      // RevenueCat app user ID is suffixed with the deployment environment
      // (e.g., "cuid123_dev") for environment isolation. See revenuecat-webhook.ts.
      const rc_user_id = `${current_user.cuid}_${app_environment}`;
      const purchases = configureRevenueCat(revenuecat_api_key, rc_user_id);

      const result = await purchases.purchase({
        rcPackage,
      });

      const activeEntitlements = Object.keys(
        result.customerInfo.entitlements.active,
      );
      if (activeEntitlements.length > 0) {
        trackEvent(MixpanelEvents.PURCHASE_SUCCEEDED, {
          plan: target_tier,
          period: selectedPeriod,
        });
        toast.success(
          getPurchaseSuccessMessage(
            target_tier,
            isMonthly,
            hasTrialOffer,
            rcPackage,
          ),
        );
        await utils.users.get_current_user.invalidate();
        router.refresh();
      }
    } catch (err) {
      if (err instanceof PurchasesError) {
        if (err.errorCode === ErrorCode.UserCancelledError) return;
        trackEvent(MixpanelEvents.PURCHASE_FAILED, {
          error: err.message,
        });
        toast.error(err.message);
      } else {
        trackEvent(MixpanelEvents.PURCHASE_FAILED, {
          error: "Unknown error",
        });
        toast.error("An error occurred. Please try again later.");
      }
    } finally {
      setPurchasingTier(null);
    }
  };

  if (!billingCatalog) {
    return (
      <div className="relative overflow-hidden">
        <div className="relative container mx-auto px-4 py-24 text-center text-white/50">
          Loading pricing...
        </div>
      </div>
    );
  }

  const freePlan = getCatalogPlan(billingCatalog, E_BillingPlanKey.FREE);
  const proPlan = getCatalogPlan(billingCatalog, E_BillingPlanKey.PRO);
  const maxPlan = getCatalogPlan(billingCatalog, E_BillingPlanKey.MAX);

  if (!freePlan || !proPlan || !maxPlan) {
    return (
      <div className="relative overflow-hidden">
        <div className="relative container mx-auto px-4 py-24 text-center text-white/50">
          Pricing is temporarily unavailable.
        </div>
      </div>
    );
  }

  const proPackage = packagesByTier.PRO ?? null;
  const maxPackage = packagesByTier.MAX ?? null;
  const proPurchaseOption = getPurchaseOption(
    billingCatalog,
    E_BillingPlanKey.PRO,
    isMonthly,
  );
  const maxPurchaseOption = getPurchaseOption(
    billingCatalog,
    E_BillingPlanKey.MAX,
    isMonthly,
  );
  const usePublicPriceFallback = !is_authed;

  return (
    <div className="relative overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative container mx-auto px-4 pt-16 pb-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white">
            {billingCatalog.offering.hero_title_accent
              ? `${billingCatalog.offering.hero_title} ${billingCatalog.offering.hero_title_accent}`
              : hasTrialOffer
                ? "Start your 7-day free trial"
                : "Turn market noise into clear next steps"}
          </h1>
          <p className="mt-3 text-lg leading-7 text-muted-foreground">
            {billingCatalog.offering.hero_title_accent || hasTrialOffer
              ? billingCatalog.offering.hero_body
              : "More Prism research, smarter alerts, and portfolio insight when you need to act."}
          </p>
          {billingCatalog.offering.promo_badge ? (
            <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              {billingCatalog.offering.promo_badge}
            </p>
          ) : null}
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-20">
          {/* Billing period toggle — spans full width */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-3 flex justify-center mb-4"
          >
            <div className="flex w-fit rounded-full bg-muted/50 p-1">
              {billing_periods.map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={cn(
                    "relative w-fit px-5 py-2.5 text-base font-semibold text-foreground transition-colors",
                    period === "Annual"
                      ? "flex items-center justify-center gap-2.5"
                      : "",
                  )}
                >
                  <span className="relative z-10">{period}</span>
                  {period === selectedPeriod && (
                    <motion.span
                      layoutId="tab"
                      transition={{ type: "spring", duration: 0.4 }}
                      className="absolute inset-0 z-0 rounded-full bg-background shadow-sm"
                    />
                  )}
                  {period === "Annual" && (
                    <Badge
                      className={cn(
                        "relative z-10 whitespace-nowrap text-sm shadow-none",
                        period === selectedPeriod
                          ? "bg-primary/15 text-primary hover:bg-primary/15"
                          : "bg-muted text-muted-foreground hover:bg-muted",
                      )}
                    >
                      Save
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </motion.div>

          <CurrentPlanCard tier={user_tier} isAuthenticated={is_authed} />

          {/* ── Free Card ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl p-7 lg:p-8 border border-white/10 h-full flex flex-col">
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-white/60 mb-2">
                  {freePlan.label}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white/60">$0</span>
                  <span className="text-base font-semibold text-white/40">
                    /{isMonthly ? "month" : "year"}
                  </span>
                </div>
                <p className="mt-4 text-base leading-6 text-white/55">
                  {freePlan.blurb}
                </p>
              </div>

              <div className="space-y-5 flex-1">
                {freePlan.features.map((feature) => (
                  <div
                    key={feature.label}
                    className="flex items-start gap-3 min-h-10"
                  >
                    {feature.state === E_BillingPlanFeatureState.INCLUDED ? (
                      <Check className="w-5 h-5 text-white/35 mt-0.5 shrink-0" />
                    ) : (
                      <Lock className="w-5 h-5 text-white/25 mt-0.5 shrink-0" />
                    )}
                    <p
                      className={cn(
                        "text-base leading-6",
                        feature.state === E_BillingPlanFeatureState.INCLUDED
                          ? "text-white/50"
                          : "text-white/35",
                      )}
                    >
                      {feature.label}
                    </p>
                  </div>
                ))}
              </div>

              {!is_authed && (
                <Button
                  className="w-full mt-8 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  variant="outline"
                  onClick={() => {
                    window.location.href = "/api/auth/sign-in?redirect=pricing";
                  }}
                >
                  Sign in to start
                </Button>
              )}
            </div>
          </motion.div>

          {/* ── Pro Card ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            {user_tier === "FREE" && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                <Badge className="bg-linear-to-r from-primary to-purple-500 text-white border-0 px-4 py-1">
                  MOST POPULAR
                </Badge>
              </div>
            )}

            <div
              className={cn(
                "relative backdrop-blur-xl rounded-2xl p-7 lg:p-8 h-full flex flex-col transition-transform",
                user_tier === "FREE"
                  ? "bg-linear-to-b from-primary/20 to-primary/5 border-2 border-primary/50 hover:scale-[1.02]"
                  : "bg-white/5 border border-white/10",
              )}
            >
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h3 className="text-2xl font-semibold text-white">
                    {proPlan.label}
                  </h3>
                </div>
                <PlanPriceDisplay
                  pkg={proPackage}
                  tier={E_BillingPlanKey.PRO}
                  isMonthly={isMonthly}
                  usePublicFallback={usePublicPriceFallback}
                />
                <p className="mt-4 text-base font-semibold leading-6 text-primary/90">
                  {getPlanBillingNote(
                    proPackage,
                    E_BillingPlanKey.PRO,
                    proPurchaseOption,
                    isMonthly,
                    hasTrialOffer,
                    usePublicPriceFallback,
                  )}
                </p>
              </div>

              <div className="space-y-5 flex-1">
                {proPlan.features.map((feature) => (
                  <div
                    key={feature.label}
                    className="flex items-start gap-3 min-h-10"
                  >
                    <div className="rounded-full bg-green-500/20 p-1 shrink-0">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    <p className="text-base leading-6 text-white/75">
                      {feature.label}
                    </p>
                  </div>
                ))}
              </div>

              <ProCardCTA
                user_tier={user_tier}
                is_authed={is_authed}
                accountPending={accountPending}
                purchasingTier={purchasingTier}
                hasTrialOffer={hasTrialOffer}
                isPackageAvailable={proPackage !== null}
                productsLoading={productsLoading}
                onUpgrade={() => handleUpgrade("PRO")}
              />
            </div>
          </motion.div>

          {/* ── Max Card ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative"
          >
            {user_tier === "PRO" && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                <Badge className="bg-linear-to-r from-amber-500 to-orange-500 text-white border-0 px-4 py-1">
                  UPGRADE
                </Badge>
              </div>
            )}

            <div
              className={cn(
                "relative backdrop-blur-xl rounded-2xl p-7 lg:p-8 h-full flex flex-col transition-transform",
                user_tier !== "MAX"
                  ? "bg-linear-to-b from-amber-500/15 to-amber-500/5 border-2 border-amber-500/40 hover:scale-[1.02]"
                  : "bg-white/5 border border-white/10",
              )}
            >
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="h-5 w-5 text-amber-400" />
                  <h3 className="text-2xl font-semibold text-white">
                    {maxPlan.label}
                  </h3>
                </div>
                <PlanPriceDisplay
                  pkg={maxPackage}
                  tier={E_BillingPlanKey.MAX}
                  isMonthly={isMonthly}
                  usePublicFallback={usePublicPriceFallback}
                />
                <p className="mt-4 text-base font-semibold leading-6 text-amber-400/90">
                  {getPlanBillingNote(
                    maxPackage,
                    E_BillingPlanKey.MAX,
                    maxPurchaseOption,
                    isMonthly,
                    hasTrialOffer,
                    usePublicPriceFallback,
                  )}
                </p>
              </div>

              <div className="space-y-5 flex-1">
                {maxPlan.features.map((feature) => (
                  <div
                    key={feature.label}
                    className="flex items-start gap-3 min-h-10"
                  >
                    <div className="rounded-full bg-amber-500/20 p-1 shrink-0">
                      <Check className="w-4 h-4 text-amber-400" />
                    </div>
                    <p className="text-base leading-6 text-white/75">
                      {feature.label}
                    </p>
                  </div>
                ))}
              </div>

              <MaxCardCTA
                user_tier={user_tier}
                is_authed={is_authed}
                accountPending={accountPending}
                purchasingTier={purchasingTier}
                hasTrialOffer={hasTrialOffer}
                isPackageAvailable={maxPackage !== null}
                productsLoading={productsLoading}
                onUpgrade={() => handleUpgrade("MAX")}
              />
            </div>
          </motion.div>

          {hasTrialOffer ? (
            <p className="md:col-span-3 mx-auto mt-2 max-w-2xl text-center text-sm leading-6 text-white/40">
              Trials convert to paid subscriptions automatically unless canceled
              before the trial ends. Manage or cancel anytime from account
              settings.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CTA Buttons
// ============================================================================

function ProCardCTA({
  user_tier,
  is_authed,
  accountPending,
  purchasingTier,
  hasTrialOffer,
  isPackageAvailable,
  productsLoading,
  onUpgrade,
}: {
  user_tier: T_Tier;
  is_authed: boolean;
  accountPending: boolean;
  purchasingTier: "PRO" | "MAX" | null;
  hasTrialOffer: boolean;
  isPackageAvailable: boolean;
  productsLoading: boolean;
  onUpgrade: () => void;
}) {
  if (!is_authed) {
    return (
      <Button
        className="w-full mt-8 bg-green-700 hover:bg-green-800 text-white font-semibold text-lg py-6 shadow-lg shadow-green-700/25"
        onClick={() => {
          window.location.href = "/api/auth/sign-in?redirect=pricing";
        }}
      >
        Sign in to start
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    );
  }

  if (TIER_RANK[user_tier] >= TIER_RANK.PRO) {
    if (user_tier === "PRO") {
      return (
        <Button asChild className="w-full mt-8 bg-white/10 hover:bg-white/20">
          <Link prefetch={false} href={createWebSettingsHref("subscription")}>
            Current Plan
          </Link>
        </Button>
      );
    }
    // User is on MAX — no Pro button needed
    return null;
  }

  return (
    <Button
      className="w-full mt-8 bg-green-700 hover:bg-green-800 text-white font-semibold text-lg py-6 shadow-lg shadow-green-700/25"
      onClick={onUpgrade}
      loading={purchasingTier === "PRO"}
      disabled={
        purchasingTier !== null ||
        accountPending ||
        productsLoading ||
        !isPackageAvailable
      }
    >
      {productsLoading
        ? "Loading Pro..."
        : hasTrialOffer
          ? "Start 7-day Pro trial"
          : "Subscribe to Pro"}
      <ChevronRight className="ml-2 h-5 w-5" />
    </Button>
  );
}

function MaxCardCTA({
  user_tier,
  is_authed,
  accountPending,
  purchasingTier,
  hasTrialOffer,
  isPackageAvailable,
  productsLoading,
  onUpgrade,
}: {
  user_tier: T_Tier;
  is_authed: boolean;
  accountPending: boolean;
  purchasingTier: "PRO" | "MAX" | null;
  hasTrialOffer: boolean;
  isPackageAvailable: boolean;
  productsLoading: boolean;
  onUpgrade: () => void;
}) {
  if (!is_authed) {
    return (
      <Button
        className="w-full mt-8 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-lg py-6 shadow-lg shadow-amber-600/25"
        onClick={() => {
          window.location.href = "/api/auth/sign-in?redirect=pricing";
        }}
      >
        Sign in to start
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    );
  }

  if (user_tier === "MAX") {
    return (
      <Button asChild className="w-full mt-8 bg-white/10 hover:bg-white/20">
        <Link prefetch={false} href={createWebSettingsHref("subscription")}>
          Current Plan
        </Link>
      </Button>
    );
  }

  return (
    <Button
      className="w-full mt-8 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-lg py-6 shadow-lg shadow-amber-600/25"
      onClick={onUpgrade}
      loading={purchasingTier === "MAX"}
      disabled={
        purchasingTier !== null ||
        accountPending ||
        productsLoading ||
        !isPackageAvailable
      }
    >
      {productsLoading
        ? "Loading Max..."
        : user_tier === "PRO"
          ? "Upgrade to Max"
          : hasTrialOffer
            ? "Start 7-day Max trial"
            : "Upgrade to Max"}
      <ChevronRight className="ml-2 h-5 w-5" />
    </Button>
  );
}
